import { FontAwesome } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  Pressable,
  useWindowDimensions,
  Dimensions,
  ScrollView,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { DatePickerModal } from "react-native-paper-dates";
import { useDispatch } from "react-redux";

import Title from "@/components/Title";
import TransactionsItems from "@/components/management/TransactionsItems";
import ExchangeChart from "@/components/management/charts/ExchangeChart";
import ExchangeSellChart from "@/components/management/charts/ExchangeSellChart";
import FeesTypeChart from "@/components/management/charts/FeesTypeChart";
import PerWeekChart from "@/components/management/charts/PerWeekChart";
import RailChart from "@/components/management/charts/RailChart";
import RailTransferInChart from "@/components/management/charts/RailTransferInChart";
import { setForm } from "@/context/actions/actions";
import i18n from "@/locales/i18n";
import { sendRequest } from "@/utils/ApiUtil";
import { generateAdminStatement } from "@/utils/transactions/ExportBankStatement";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDate, setOpenDate] = useState(false);
  const [startDateSelected, setStartDateSelected] = useState<Date | null>(null);
  const [endDateSelected, setEndDateSelected] = useState<Date | null>(null);
  const [optionSelected, setOptionSelected] = useState("CHARTS");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const date = new Date();
  const currentDate = new Date();
  const pastDate = new Date(currentDate.getTime());
  pastDate.setDate(currentDate.getDate() - 30);

  useEffect(() => {
    const updateLayout = () => {
      const screenDimensions = Dimensions.get("window");
      setIsMobile(screenDimensions.width > 1024);
    };

    updateLayout();
    const subscription = Dimensions.addEventListener("change", updateLayout);
    return () => subscription.remove();
  }, []);

  const getTransactions = async (range: any) => {
    setOpenDate(false);
    setTransactions([]);

    const { startDate, endDate } = range;
    const startDateObject = new Date(startDate);
    const endDateObject = new Date(endDate);

    const startDateFormatted = startDateObject.toISOString().split("T")[0];
    const endDateFormatted = endDateObject.toISOString().split("T")[0];

    try {
      setIsLoading(true);
      const request = await sendRequest(
        "get",
        "/admin/all-transactions",
        { startDate: startDateFormatted, endDate: endDateFormatted },
        true,
        true
      );
      if (request) {
        setTransactions(request);
        setIsLoading(false);
        setStartDateSelected(startDateObject);
        setEndDateSelected(endDateObject);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      dispatch(
        setForm({ currentMenuItem: t("management/home:menu.transactions") })
      );
      setStartDateSelected(pastDate);
      setEndDateSelected(currentDate);
      getTransactions({
        startDate: pastDate,
        endDate: currentDate,
      });
      return () => {
        setIsLoading(false);
        setTransactions([]);
        setStartDateSelected(null);
        setEndDateSelected(null);
        setOptionSelected("CHARTS");
      };
    }, [])
  );

  const formatDate = (date: Date) => {
    return date.toLocaleString(i18n.language, {
      month: "long",
      day: "numeric",
    });
  };

  return (
    <ScrollView
      style={{
        backgroundColor: "#FFFFFF",
        paddingHorizontal: 16,
        paddingVertical: 8,
        flexGrow: 1,
      }}
    >
      <View className="flex-col w-full items-center">
        <Title text={t("management/transactions:title")} />
      </View>
      <View className="w-full h-fit items-center border border-gray rounded-lg p-4 mt-4 shadow">
        <Pressable
          onPress={() => setOpenDate(true)}
          className="flex-row border border-solid rounded-lg border-midgray items-center justify-between w-80 h-14 px-4"
        >
          <Text className="text-base text-midgray">
            {t("common:placeholder.selectdaterange")}
          </Text>
          <FontAwesome name="calendar-o" size={24} color="#BDBDBD" />
        </Pressable>
        <Pressable
          className={`items-center justify-center w-60 h-8 rounded-full p-2 mt-6 ${
            transactions.length === 0
              ? "bg-midgray"
              : "bg-purple1 hover:bg-purple3"
          }`}
          onPress={() => generateAdminStatement(transactions)}
          disabled={transactions.length === 0}
        >
          <Text className="text-white font-normal text-base">
            {t("common:button.download")}
          </Text>
        </Pressable>
        <View className="w-full items-center justify-center flex-row mt-4">
          {startDateSelected && (
            <Text className="text-darkergray font-light text-base mx-4">
              {`${t("common:terms.startdate")}: ${formatDate(
                startDateSelected
              )}`}
            </Text>
          )}
          {endDateSelected && (
            <Text className="text-darkergray font-light text-base mx-4">
              {`${t("common:terms.enddate")}: ${formatDate(endDateSelected)}`}
            </Text>
          )}
        </View>
        <DatePickerModal
          locale="en"
          mode="range"
          visible={openDate}
          startDate={date}
          endDate={date}
          onDismiss={() => setOpenDate(false)}
          date={date}
          onConfirm={(params) => getTransactions(params)}
          animationType="fade"
          presentationStyle="pageSheet"
          label={t("common:placeholder.selectdate")}
          saveLabel={t("common:button.save")}
        />
      </View>
      <View className="flex-row w-full items-center justify-center my-6">
        <Pressable
          className={`${
            optionSelected === "CHARTS"
              ? "border-b border-purple1"
              : "border-b-none"
          } mr-4`}
          onPress={() => setOptionSelected("CHARTS")}
        >
          <Text
            className={`${
              optionSelected === "CHARTS" ? "text-purple1" : "text-midgray"
            } text-lg`}
          >
            {t("common:terms.charts")}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setOptionSelected("TRANSACTIONS")}
          className={`${
            optionSelected === "TRANSACTIONS"
              ? "border-b border-purple1"
              : "border-b-none"
          } mr-4`}
        >
          <Text
            className={`${
              optionSelected === "TRANSACTIONS"
                ? "text-purple1"
                : "text-midgray"
            } text-lg`}
          >
            {t("common:terms.transactions")}
          </Text>
        </Pressable>
      </View>
      {isLoading ? (
        <View className="w-full items-center mt-4">
          <ActivityIndicator
            size="large"
            animating={isLoading}
            color="#5D0CBA"
          />
        </View>
      ) : (
        <View>
          {transactions.length > 0 &&
            (optionSelected === "CHARTS" ? (
              <View className="w-full items-center justify-center flex-col">
                <View className="w-full items-center justify-center flex-col lg:flex-row">
                  <RailChart
                    data={transactions ?? []}
                    width={width}
                    isMobile={isMobile}
                  />
                  <RailTransferInChart
                    data={transactions ?? []}
                    width={width}
                    isMobile={isMobile}
                  />
                </View>
                <View className="w-full h-fit items-center justify-center flex-col lg:flex-row">
                  <PerWeekChart
                    data={transactions ?? []}
                    startDate={startDateSelected}
                    endDate={endDateSelected}
                    width={width}
                    isMobile={isMobile}
                  />
                  <FeesTypeChart
                    data={transactions ?? []}
                    width={width}
                    isMobile={isMobile}
                  />
                </View>
                <View className="w-full h-fit items-center justify-center flex-col lg:flex-row">
                  <ExchangeChart
                    data={transactions ?? []}
                    width={width}
                    isMobile={isMobile}
                  />
                  <ExchangeSellChart
                    data={transactions ?? []}
                    width={width}
                    isMobile={isMobile}
                  />
                </View>
              </View>
            ) : (
              <View className="w-full">
                <TransactionsItems data={transactions ?? []} />
              </View>
            ))}
        </View>
      )}
    </ScrollView>
  );
}
